import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { memo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { AngleIcon, ExternalLinkIcon } from '../../../../components/Icons'
import { ShareButtons } from '../../../../components/ShareButtons'
import { IUserData } from '../../../../store/reducers/userReducer'
import { Heading } from '../Heading'
import { Privacy } from '../Privacy'
import { RemoveTwin } from '../RemoveTwin'

export interface TwinReadyContentProps {
  user: IUserData
  locationSearch: string | null
  twinImageUrl: string
}

const TwinReadyContent: React.FC<TwinReadyContentProps> = memo(
  ({ user, locationSearch, twinImageUrl }) => {
    const [searchParams] = useSearchParams()
    const isMacos = searchParams.get('platform') === 'macos'

    return (
      <>
        <img
          className="w-24 h-24 rounded-full object-cover mb-2"
          src={twinImageUrl}
          alt="Twin"
        />

        <Heading title="Your Digital Twin is Ready" />
        <div className="my-0 sm:my-14 flex gap-4 flex-col sm:flex-row w-full justify-center">
          <Menu>
            <MenuButton className="btn btn-primary focus:outline-none block">
              Edit Your Twin
              <div className="-rotate-90 ml-2">
                <AngleIcon />
              </div>
            </MenuButton>
            <MenuItems
              modal={false}
              anchor="bottom"
              className="[--anchor-gap:8px] w-[var(--button-width)] shadow-lg z-50 tc-mv-blur rounded-2xl p-2 focus:outline-none"
              transition
            >
              <MenuItem
                as={Link}
                to="/generate-twin/knowledge/files"
                className="w-full px-4 py-2 rounded-lg text-sm text-gray-200 hover:bg-black hover:bg-opacity-20 flex items-center transition-colors duration-150"
              >
                Knowledge Base
              </MenuItem>
              <MenuItem
                as={Link}
                to="/generate-twin/knowledge/faq"
                className="w-full px-4 py-2 rounded-lg text-sm text-gray-200 hover:bg-black hover:bg-opacity-20 flex items-center transition-colors duration-150"
              >
                FAQ
              </MenuItem>
            </MenuItems>
          </Menu>

          <Link
            data-testid="talk-with-twin-link"
            to={
              isMacos
                ? `napster-companion://companion/start/${user?.mentorId}`
                : `/${user.mentorSlug}${locationSearch ? `${locationSearch}&start=true` : '?start=true'}`
            }
            className="btn btn-primary block"
          >
            Talk With Your Twin&nbsp;
            <ExternalLinkIcon size={16} />
          </Link>
        </div>

        <h3 className="text-xl mt-4 sm:mt-8">Share Your Twin</h3>
        <p className="text-center max-w-[600px] mx-auto">
          Share a link so people can meet your AI twin. It answers in your voice
          within the topics you choose. You can turn sharing off anytime.
        </p>

        <div className="mt-4">
          <ShareButtons slug={user.mentorSlug} />
        </div>
        <div className="mb-auto sm:mb-0">
          <RemoveTwin />
        </div>
        <Privacy />
      </>
    )
  },
)

export { TwinReadyContent }
